import { useMemo } from "react";
import { useTellescopeSession } from "../context";
import { buildTellescopeQueryKey } from "../keys";
import { ModelName, ParamsForMethod, QueryMethodForModel, SupportedSession, TellescopeQueryKey } from "../types";
import { LimitedQueryOptions } from "./types";
import { useQuery } from "@tanstack/react-query";
import { addKeyToQueryResult, executeTellescopeApiMethod } from "../utils";

export type TellescopeCountQueryRequest<
  Model extends ModelName,
  Method extends QueryMethodForModel<Model> = Extract<QueryMethodForModel<Model>, "getSome">,
> = Omit<ParamsForMethod<Model, Method>, "returnCount">;

export interface TellescopeCountQueryResponse {
  count: number;
}

export interface UseTellescopeCountQueryOptions<
  Model extends ModelName,
  Method extends QueryMethodForModel<Model> = Extract<QueryMethodForModel<Model>, "getSome">,
  TError = Error,
  TData = TellescopeCountQueryResponse,
> extends LimitedQueryOptions<TellescopeCountQueryResponse, TError, TData, TellescopeQueryKey<Model, Method>> {
  model: Model;
  method?: Method;
  request: TellescopeCountQueryRequest<Model, Method>;
  session?: SupportedSession;
  requestKeysToOmitFromQueryKey?: (keyof TellescopeCountQueryRequest<Model, Method>)[];
  requestSideEffect?: (request: TellescopeCountQueryRequest<Model, Method>) => void;
  responseSideEffect?: (
    request: TellescopeCountQueryRequest<Model, Method>,
    response: TellescopeCountQueryResponse
  ) => void;
}

export const useTellescopeCountQuery = <
  Model extends ModelName,
  Method extends QueryMethodForModel<Model> = Extract<QueryMethodForModel<Model>, "getSome">,
  TError = Error,
  TData = TellescopeCountQueryResponse,
>({
  model,
  method: providedMethod,
  request: providedRequest,
  session: providedSession,
  requestKeysToOmitFromQueryKey,
  requestSideEffect,
  responseSideEffect,
  ...tanstackOptions
}: UseTellescopeCountQueryOptions<Model, Method, TError, TData>) => {
  const session = useTellescopeSession(providedSession);
  const method: Method = providedMethod ?? ("getSome" as Method);
  // `providedRequest` is just `ParamsForMethod` without `returnCount` so we can safely type assert
  const request = useMemo(
    () =>
      ({
        ...providedRequest,
        returnCount: true,
      }) as unknown as ParamsForMethod<Model, Method>,
    [providedRequest]
  );

  const queryKey = useMemo(
    () =>
      buildTellescopeQueryKey<Model, Method>({
        model,
        method: `${method}-count`,
        request,
        requestKeysToOmitFromQueryKey,
      }),
    [method, model, request, requestKeysToOmitFromQueryKey]
  );

  const queryResult = useQuery({
    queryKey,
    queryFn: async () => {
      if (session === null) {
        throw new Error("No Tellescope session provided for query. Queries should be disabled is session is `null`");
      }

      requestSideEffect?.(request);
      const res = await executeTellescopeApiMethod<Model, Method, true>({
        session,
        model,
        method,
        request,
        parseISOStrings: true, // TODO: improve this by making it an option locally and globally
      });
      responseSideEffect?.(request, res);
      return res;
    },
    ...tanstackOptions,
    enabled: !!session && (tanstackOptions?.enabled ?? true),
  });

  return addKeyToQueryResult(queryResult, queryKey);
};
